import { registerPlugin, Capacitor } from '@capacitor/core';

// Raw ESC/POS transport for printers with no ePOS-Print service — the Epson
// TM-T20X being the case that prompted this. Mirrors eposClient's contract
// deliberately: never throws, always resolves to {success, reason}, and uses
// the same reason vocabulary so callers do not need to know which protocol
// carried the job.
//
// There is no read path. Port 9100 is write-only: the printer answers
// nothing. A successful result therefore means "the bytes were accepted by
// the socket", NOT "paper came out" — unlike ePOS, which returns a status
// document. UI copy must not promise more than that.
const AqanaTcpPrint = registerPlugin('AqanaTcpPrint');

export const RAW_PORT = 9100;

// Maps the Swift plugin's rejection codes onto eposClient's reasons.
// WRITE_FAILED becomes device_error because by that point the socket was
// open — the printer accepted a connection and then refused the bytes,
// which is a device problem rather than a network one.
const REASON_BY_CODE = {
    TIMEOUT: 'timeout',
    CONNECT_FAILED: 'network_error',
    BAD_ARGS: 'network_error',
    NO_SOCKET: 'device_error',
    WRITE_FAILED: 'device_error',
};

const reasonFor = (err) => REASON_BY_CODE[err && err.code] || 'network_error';

export function isRawPrintAvailable() {
    return Capacitor.isNativePlatform();
}

// Three outcomes, not two. 'unavailable' exists because conflating "the
// plugin isn't there" with "the port is shut" would make a build with a
// registration problem look exactly like a printer that cannot do raw
// printing — and we would wrongly abandon a working approach.
export const RAW_OPEN = 'open';
export const RAW_CLOSED = 'closed';
export const RAW_UNAVAILABLE = 'unavailable';

export async function probeRawPort(ip, port = RAW_PORT, opts = {}) {
    const { timeoutMs = 3000 } = opts;

    if (!isRawPrintAvailable()) {
        // Web/portal build — there is no native socket here at all.
        return RAW_UNAVAILABLE;
    }

    try {
        const result = await AqanaTcpPrint.probe({
            host: ip, port: port || RAW_PORT, timeoutMs,
        });
        return result && result.open ? RAW_OPEN : RAW_CLOSED;
    } catch (e) {
        // The native probe resolves {open:false} for a shut port — it only
        // ever rejects on a structural problem (plugin not registered, bad
        // arguments). So a rejection is never evidence about the port.
        //
        return RAW_UNAVAILABLE;
    }
}

export async function sendRawToPrinter(ip, port, dataBase64, opts = {}) {
    const { timeoutMs = 5000 } = opts;

    if (!isRawPrintAvailable()) {
        return { success: false, reason: 'unavailable' };
    }

    let socketId = null;
    try {
        const opened = await AqanaTcpPrint.connect({
            host: ip, port: port || RAW_PORT, timeoutMs,
        });
        socketId = opened.socketId;
        await AqanaTcpPrint.write({ socketId, dataBase64 });
        return { success: true, reason: null };
    } catch (err) {
        return { success: false, reason: reasonFor(err) };
    } finally {
        if (socketId) {
            // Never let a failed write leak a connection — printers accept
            // a small, fixed number of concurrent sockets, and a leaked one
            // locks out every later job until the printer is power-cycled.
            try {
                await AqanaTcpPrint.close({ socketId });
            } catch (e) {
                // Closing is best-effort. Throwing here would replace a
                // useful failure reason with a meaningless one.
            }
        }
    }
}
